import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Collapse, IconButton, InputAdornment, Link, MenuItem, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { ROUTES } from "../../common/constant/routes";
import { formatDate } from "../../common/constant/dates";
import { AdminActionEntry, getActivity } from "../../common/requests/activity/endpoints";
import { Banner, EmptyState, ErrorState, LoadingRows, PageShell, Panel, StatusPill, errorMessage } from "../../ui";

const CATEGORIES: { value: string; label: string }[] = [
  { value: "all", label: "Everything" },
  { value: "order.", label: "Orders" },
  { value: "withdrawal.", label: "Payouts" },
  { value: "user.", label: "Users & sellers" },
  { value: "admin.", label: "Admin access" },
  { value: "invite.", label: "Seller invites" },
  { value: "notification.", label: "Messages" },
  { value: "search.", label: "Search acronyms" },
  { value: "promotion.", label: "Promotions" },
];

const CATEGORY_LABEL: Record<string, string> = {
  order: "Order",
  withdrawal: "Payout",
  user: "User",
  admin: "Access",
  invite: "Invite",
  notification: "Message",
  search: "Search",
  promotion: "Promotion",
};

const timeLabel = (iso: string) => {
  const d = new Date(iso);
  return `${formatDate(d)} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

/** Where the thing an action touched lives in the dashboard, if anywhere. */
function targetLink(entry: AdminActionEntry): { to: string; label: string } | null {
  if (!entry.target_id && entry.target_type !== "broadcast") return null;
  const id = encodeURIComponent(entry.target_id ?? "");
  switch (entry.target_type) {
    case "order":
      return { to: `${ROUTES.SALES}?q=${id}&order=${id}`, label: "Open order" };
    case "withdrawal":
      return { to: `${ROUTES.WITHDRAWALS}?view=all&payout=${id}`, label: "Open payout" };
    case "user":
      return { to: `${ROUTES.USER_MANAGEMENT}?tab=users&user=${id}`, label: "Open user" };
    case "admin_user":
      return { to: ROUTES.USER_MANAGEMENT, label: "Open users" };
    case "invite":
      return { to: `${ROUTES.USER_MANAGEMENT}?tab=invites`, label: "Open invites" };
    case "promotion":
      return { to: `${ROUTES.PROMOTIONS}?promotion=${id}`, label: "Open promotion" };
    case "broadcast":
      return { to: `${ROUTES.USER_MANAGEMENT}?tab=messages`, label: "Open messages" };
    default:
      return null;
  }
}

function DetailsGrid({ details }: { details: Record<string, any> }) {
  const entries = Object.entries(details).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!entries.length) return null;
  return (
    <Box
      component="dl"
      sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(96px, max-content) minmax(0, 1fr)" }, columnGap: 1.5, rowGap: 0.5 }}
    >
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <Typography component="dt" variant="caption" sx={{ color: C.muted, fontWeight: 600 }}>
            {key.replaceAll("_", " ")}
          </Typography>
          <Typography component="dd" variant="caption" sx={{ m: 0, mb: { xs: 0.75, sm: 0 }, whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: C.ink }}>
            {typeof value === "object" ? JSON.stringify(value, null, 1) : String(value)}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

function ActivityRow({ entry }: { entry: AdminActionEntry }) {
  const [open, setOpen] = React.useState(false);
  const link = targetLink(entry);
  const category = entry.action.split(".")[0];
  const hasDetails = Object.keys(entry.details ?? {}).length > 0;
  return (
    <Box component="li" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, borderTop: `1px solid ${C.line}`, "&:first-of-type": { borderTop: 0 } }}>
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.25 }}>
            <StatusPill tone="outline">{CATEGORY_LABEL[category] ?? category}</StatusPill>
            <Typography variant="caption" className="num" sx={{ color: C.muted }}>
              {timeLabel(entry.created_at)}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>
            {entry.summary}
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: C.muted, overflowWrap: "anywhere" }}>
            {entry.admin_email ?? entry.admin_user_id}
            {link && (
              <>
                {" · "}
                <Link component={RouterLink} to={link.to} underline="hover" sx={{ color: C.ink, fontWeight: 600 }}>
                  {link.label}
                </Link>
              </>
            )}
          </Typography>
        </Box>
        {hasDetails && (
          <IconButton
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? "Hide details" : "Show details"}
            sx={{ width: 44, height: 44, mr: -1, mt: -0.5 }}
          >
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1, p: 1.25, bgcolor: C.head, border: `1px solid ${C.line}`, borderRadius: "6px" }}>
          <DetailsGrid details={entry.details} />
        </Box>
      </Collapse>
    </Box>
  );
}

/** Who did what in the dashboard, newest first (superadmins only). */
export default function Activity() {
  const { idToken } = useAuth();
  const [category, setCategory] = React.useState("all");
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [entries, setEntries] = React.useState<AdminActionEntry[]>([]);
  const [nextBefore, setNextBefore] = React.useState<string | null>(null);
  const [available, setAvailable] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = React.useCallback(
    async (before?: string) => {
      if (!idToken) return;
      before ? setLoadingMore(true) : setLoading(true);
      try {
        const { data } = await getActivity(idToken, {
          limit: 50,
          before,
          action: category === "all" ? undefined : category,
          q: debouncedQuery || undefined,
        });
        setAvailable(data.available);
        setEntries((prev) => (before ? [...prev, ...data.actions] : data.actions));
        setNextBefore(data.next_before);
        setError(null);
      } catch (err: any) {
        setError(errorMessage(err, "Couldn't load the activity log."));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [idToken, category, debouncedQuery]
  );

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <PageShell title="Activity log" subtitle="Every change made in this dashboard: who did it, when, and what changed.">
      {!available && (
        <Banner title="Not installed yet.">
          Run <code>admin-backend/sql/migrations/001_admin_actions.sql</code> on the database; actions are recorded from then on.
        </Banner>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <TextField
          select
          size="small"
          label="Show"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ flex: { xs: "1 1 100%", sm: "0 0 200px" } }}
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          placeholder="Search what happened or who did it"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{
            htmlInput: { "aria-label": "Search activity" },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 240px" } }}
        />
      </Box>

      {error && entries.length > 0 && (
        <Banner
          tone="danger"
          action={
            <Button variant="outlined" size="small" onClick={() => load()}>
              Retry
            </Button>
          }
        >
          {error}
        </Banner>
      )}

      <Panel padding={0} sx={{ overflow: "hidden" }}>
        {loading ? (
          <LoadingRows rows={5} height={56} />
        ) : error && entries.length === 0 ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : entries.length === 0 ? (
          <EmptyState title={available ? "Nothing recorded for this filter yet." : "Nothing recorded yet."} />
        ) : (
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {entries.map((entry) => (
              <ActivityRow key={entry.id} entry={entry} />
            ))}
          </Box>
        )}
      </Panel>

      {nextBefore && !loading && (
        <Button variant="outlined" onClick={() => load(nextBefore)} disabled={loadingMore} sx={{ alignSelf: "center", minHeight: 44, minWidth: 160 }}>
          {loadingMore ? "Loading…" : "Load older"}
        </Button>
      )}
    </PageShell>
  );
}
